import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import * as Yup from 'yup'

import { WizardHeader } from '../../../../../common/components/wizardHeader/WizardHeader'

import {
  QuestionBuilder,
  SelectOptionCard,
  SurveyMode,
  SurveyQuestion,
  SurveyQuestionType,
  SurveyType
} from '@percent/domain/volunteering'
import {
  AcknowledgeModal,
  Box,
  Flex,
  FullPageFormLayout,
  FormField,
  Loader,
  Modal,
  Select,
  Spacer,
  TextInput,
  getInputStatus
} from '@percent/lemonade'
import { ErrorView } from '@percent/partner-dashboard/common/components'
import { useMutation, useQuery } from '@percent/partner-dashboard/common/hooks'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { SurveyTemplateContent } from '@percent/partner-dashboard/services/templates/surveyTemplateService.types'
import styles from './SurveyTemplateForm.module.scss'

const TEMPLATE_NAME_MAX_CHARS = 255
const QUESTION_LABEL_MAX_CHARS = 255
const RATING_LABEL_MAX_CHARS = 100
const MAX_PRE_EVENT_QUESTIONS = 5
const MAX_POST_EVENT_QUESTIONS = 30
// Volunteers open this link in a browser, so only accept HTTP(S). Kept in sync with the externalUrl Yup schema.
const HTTP_URL_PATTERN = /^https?:\/\//
// Returning here remounts TemplatesContainer, which refetches the list on mount.
const LIST_ROUTE = '/workplace-giving/templates?tab=survey'

type SurveyTemplateFormValues = {
  name: string
  surveyType: SurveyType
  mode: SurveyMode
  surveyQuestions: SurveyQuestion[]
  externalUrl: string
}

const hasValidQuestionLabels = (questions: SurveyQuestion[]) =>
  questions.length > 0 && questions.every(question => question.label.trim())

export function SurveyTemplateForm() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { surveyTemplateService } = useServices()
  const { templateId } = useParams<{ templateId: string }>()
  const isEdit = !!templateId

  const [resultState, setResultState] = useState<'success' | 'failure' | undefined>(undefined)

  const getDefaultQuestion = (): SurveyQuestion => ({
    type: SurveyQuestionType.FreeText,
    label: '',
    required: false
  })

  const [{ data, isLoading: isFetching, errorMessage }] = useQuery(
    surveyTemplateService.getSurveyTemplate,
    templateId ?? '',
    isEdit,
    isEdit
  )

  const [{ isLoading: isCreating }, { apiFunc: createTemplate }] = useMutation(
    surveyTemplateService.createSurveyTemplate,
    () => setResultState('success'),
    () => setResultState('failure')
  )

  const [{ isLoading: isUpdating }, { apiFunc: updateTemplate }] = useMutation(
    surveyTemplateService.updateSurveyTemplate,
    () => setResultState('success'),
    () => setResultState('failure')
  )

  const initialName = data?.name ?? ''
  const initialSurveyType = data?.surveyType ?? SurveyType.PreEvent
  const initialMode = data?.mode ?? SurveyMode.BuiltIn
  const initialExternalUrl = data?.externalUrl ?? ''
  const initialQuestions = useMemo<SurveyQuestion[]>(
    () => (data?.questions?.length ? data.questions : [getDefaultQuestion()]),
    // getDefaultQuestion depends only on the (stable) translator; re-seed only when fetched data changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  )

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string()
          .trim()
          .required(t('errorMessage.required'))
          .max(TEMPLATE_NAME_MAX_CHARS, t('errorMessage.shouldNotExceed255')),
        surveyType: Yup.string().oneOf([SurveyType.PreEvent, SurveyType.PostEvent]).required(),
        mode: Yup.string().oneOf([SurveyMode.BuiltIn, SurveyMode.ExternalLink]).required(),
        externalUrl: Yup.string().when('mode', {
          is: SurveyMode.ExternalLink,
          then: schema =>
            schema
              .trim()
              .url(t('errorMessage.invalidUrl'))
              .matches(HTTP_URL_PATTERN, { message: t('errorMessage.invalidUrl'), excludeEmptyString: true })
              .required(t('errorMessage.required'))
        }),
        surveyQuestions: Yup.array().when(['mode', 'surveyType'], {
          is: (mode: string, surveyType: string) => mode === SurveyMode.BuiltIn && surveyType === SurveyType.PreEvent,
          then: schema =>
            schema
              .of(
                Yup.object().shape({
                  type: Yup.string().oneOf([SurveyQuestionType.FreeText]).required(t('errorMessage.required')),
                  label: Yup.string().trim().required(t('errorMessage.required')).max(QUESTION_LABEL_MAX_CHARS),
                  required: Yup.boolean()
                })
              )
              .min(1, t('errorMessage.required'))
              .max(MAX_PRE_EVENT_QUESTIONS)
              .required(t('errorMessage.required')),
          otherwise: outerSchema =>
            outerSchema.when('mode', {
              is: SurveyMode.BuiltIn,
              then: schema =>
                schema
                  .of(
                    Yup.object().shape({
                      type: Yup.string()
                        .oneOf([SurveyQuestionType.Rating, SurveyQuestionType.FreeText])
                        .required(t('errorMessage.required')),
                      label: Yup.string().trim().required(t('errorMessage.required')).max(QUESTION_LABEL_MAX_CHARS),
                      required: Yup.boolean(),
                      config: Yup.object().when('type', {
                        is: SurveyQuestionType.Rating,
                        then: configSchema =>
                          configSchema.shape({
                            min: Yup.number().required(t('errorMessage.required')),
                            max: Yup.number()
                              .required(t('errorMessage.required'))
                              .test('maxGreaterThanMin', t('errorMessage.required'), (value, context) =>
                                value ? value > context.parent.min : true
                              ),
                            minLabel: Yup.string().max(RATING_LABEL_MAX_CHARS),
                            maxLabel: Yup.string().max(RATING_LABEL_MAX_CHARS)
                          })
                      })
                    })
                  )
                  .min(1, t('errorMessage.required'))
                  .max(MAX_POST_EVENT_QUESTIONS)
                  .required(t('errorMessage.required'))
            })
        })
      }),
    [t]
  )

  const { control, handleSubmit, reset, setValue, formState } = useForm<SurveyTemplateFormValues>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: initialName,
      surveyType: initialSurveyType,
      mode: initialMode,
      surveyQuestions: initialQuestions,
      externalUrl: initialExternalUrl
    }
  })

  useEffect(() => {
    if (data) {
      reset({
        name: data.name ?? '',
        surveyType: initialSurveyType,
        mode: initialMode,
        surveyQuestions: initialQuestions,
        externalUrl: initialExternalUrl
      })
    }
  }, [data, initialSurveyType, initialMode, initialQuestions, initialExternalUrl, reset])

  const buildContent = (values: SurveyTemplateFormValues): SurveyTemplateContent =>
    values.mode === SurveyMode.ExternalLink
      ? { mode: SurveyMode.ExternalLink, externalUrl: values.externalUrl.trim() }
      : { mode: SurveyMode.BuiltIn, questions: values.surveyQuestions }

  const submit = handleSubmit(values => {
    const content = buildContent(values)

    if (isEdit && templateId) {
      updateTemplate({ templateId, name: values.name.trim(), surveyType: values.surveyType, ...content })

      return
    }

    createTemplate({ name: values.name.trim(), surveyType: values.surveyType, ...content })
  })

  const watchedName = useWatch({ control, name: 'name' })
  const watchedSurveyType = useWatch({ control, name: 'surveyType' })
  const watchedMode = useWatch({ control, name: 'mode' })
  const watchedQuestions = useWatch({ control, name: 'surveyQuestions' })
  const watchedExternalUrl = useWatch({ control, name: 'externalUrl' })
  const isPreEvent = watchedSurveyType === SurveyType.PreEvent
  const isExternalLink = watchedMode === SurveyMode.ExternalLink
  const maxQuestions = isPreEvent ? MAX_PRE_EVENT_QUESTIONS : MAX_POST_EVENT_QUESTIONS
  const allowedQuestionTypes = isPreEvent ? [SurveyQuestionType.FreeText] : undefined
  const isSaving = isCreating || isUpdating
  // Cheap "has content" guard; format/shape correctness is enforced by formState.isValid below.
  // Keeping this avoids RHF's initial isValid flash before the first validation runs.
  const isEmpty = isExternalLink
    ? !watchedName?.trim() || !watchedExternalUrl?.trim()
    : !watchedName?.trim() || !hasValidQuestionLabels(watchedQuestions ?? [])

  const isUnchanged =
    isEdit &&
    watchedName?.trim() === initialName.trim() &&
    watchedSurveyType === initialSurveyType &&
    watchedMode === initialMode &&
    (isExternalLink
      ? watchedExternalUrl?.trim() === initialExternalUrl.trim()
      : JSON.stringify(watchedQuestions) === JSON.stringify(initialQuestions))

  const showLoader = isEdit && isFetching
  const showError = isEdit && !isFetching && (!!errorMessage || !data)
  const isSubmitDisabled = isFetching || isEmpty || !formState.isValid || isUnchanged || showError

  const handleSurveyTypeChange = (newType: SurveyType) => {
    if (newType === SurveyType.PreEvent) {
      // Pre-event only supports built-in mode
      setValue('mode', SurveyMode.BuiltIn)

      // Strip rating questions, keep only free-text, and truncate to max
      const currentQuestions = watchedQuestions ?? []
      const freeTextOnly = currentQuestions.filter(q => q.type === SurveyQuestionType.FreeText)
      const truncated = freeTextOnly.slice(0, MAX_PRE_EVENT_QUESTIONS)

      // If no free-text questions remain, seed with a default free-text question
      const newQuestions =
        truncated.length > 0
          ? truncated
          : [{ type: SurveyQuestionType.FreeText, label: '', required: false } as SurveyQuestion]

      setValue('surveyQuestions', newQuestions, { shouldValidate: true })
    }
  }

  const handleBack = () => navigate(LIST_ROUTE)
  const handleFailureClose = () => setResultState(undefined)

  return (
    <Box className={styles.pageWrapper} data-testid="survey-template-form">
      <FullPageFormLayout
        actionsHeader={
          <WizardHeader
            title={
              isEdit ? t('typography.templates.form.editSurveyTitle') : t('typography.templates.form.createSurveyTitle')
            }
            onCancel={handleBack}
            onPublish={submit}
            isPublishDisabled={isSubmitDisabled}
            isLoading={isSaving}
            publishBtnCopy={isEdit ? t('button.save') : t('button.create')}
            toolTipCopy={isSubmitDisabled ? t('toolTip.requiredFields') : ''}
            publishBtnTestId="survey-template-submit-button"
            cancelBtnTestId="survey-template-cancel-button"
          />
        }
      >
        {showLoader && <Loader />}
        {showError && <ErrorView errorMessage={errorMessage} />}
        {!showLoader && !showError && (
          <Flex direction="column" className={styles.formWrapper}>
            <Controller
              control={control}
              name="surveyType"
              render={({ field }) => (
                <FormField
                  label={t('typography.templates.form.survey.surveyTypeLabel')}
                  necessity="required"
                  disabled={isEdit}
                >
                  <Select
                    name={field.name}
                    data-testid="survey-type-selector"
                    options={[
                      {
                        value: SurveyType.PreEvent,
                        label: t('typography.templates.form.survey.preEventTitle')
                      },
                      {
                        value: SurveyType.PostEvent,
                        label: t('typography.templates.form.survey.postEventTitle')
                      }
                    ]}
                    defaultValue={{
                      value: field.value,
                      label:
                        field.value === SurveyType.PreEvent
                          ? t('typography.templates.form.survey.preEventTitle')
                          : t('typography.templates.form.survey.postEventTitle')
                    }}
                    onChange={option => {
                      if (isEdit) return

                      const newType = option.value as SurveyType
                      field.onChange(newType)

                      if (newType === SurveyType.PreEvent) {
                        handleSurveyTypeChange(newType)
                      }
                    }}
                  />
                </FormField>
              )}
            />

            <Spacer size={2} axis="vertical" />

            {!isPreEvent && (
              <>
                <Controller
                  control={control}
                  name="mode"
                  render={({ field }) => (
                    <Box className={styles.modeCards}>
                      <SelectOptionCard
                        testId="survey-template-mode-builtin"
                        title={t('typography.templates.form.survey.builtInOptionTitle')}
                        description={t('typography.templates.form.survey.builtInOptionDescription')}
                        active={field.value === SurveyMode.BuiltIn}
                        onClick={() => field.onChange(SurveyMode.BuiltIn)}
                      />
                      <SelectOptionCard
                        testId="survey-template-mode-external"
                        title={t('typography.templates.form.survey.externalOptionTitle')}
                        description={t('typography.templates.form.survey.externalOptionDescription')}
                        active={field.value === SurveyMode.ExternalLink}
                        onClick={() => field.onChange(SurveyMode.ExternalLink)}
                      />
                    </Box>
                  )}
                />
                <Spacer size={2} axis="vertical" />
              </>
            )}

            <Controller
              control={control}
              name="name"
              render={({ field, fieldState }) => {
                const nameErrorMessage = fieldState.isTouched ? fieldState.error?.message : undefined

                return (
                  <FormField
                    label={t('typography.templates.form.nameLabel')}
                    necessity="required"
                    status={getInputStatus(nameErrorMessage)}
                    statusMessage={nameErrorMessage}
                  >
                    <TextInput
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder={t('typography.templates.form.survey.namePlaceholder')}
                      maxLength={TEMPLATE_NAME_MAX_CHARS}
                      data-testid="survey-template-name-input"
                    />
                  </FormField>
                )
              }}
            />

            <Spacer size={2} axis="vertical" />

            {isExternalLink && (
              <Controller
                control={control}
                name="externalUrl"
                render={({ field, fieldState }) => {
                  const urlErrorMessage = fieldState.isTouched ? fieldState.error?.message : undefined

                  return (
                    <FormField
                      label={t('typography.templates.form.survey.externalUrlLabel')}
                      necessity="required"
                      description={t('typography.templates.form.survey.externalUrlDescription')}
                      status={getInputStatus(urlErrorMessage)}
                      statusMessage={urlErrorMessage}
                    >
                      <TextInput
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder={t('typography.templates.form.survey.externalUrlPlaceholder')}
                        data-testid="survey-template-external-url-input"
                      />
                    </FormField>
                  )
                }}
              />
            )}

            {!isExternalLink && (
              <Box className={styles.questionsWrapper}>
                <Controller
                  control={control}
                  name="surveyQuestions"
                  render={({ field, fieldState }) => {
                    const questionErrors = Array.isArray(fieldState.error)
                      ? (fieldState.error as unknown as Array<{ label?: { message?: string } } | undefined>).map(
                          error => (error?.label ? { label: error.label.message } : undefined)
                        )
                      : undefined
                    const questionsTouched = fieldState.isTouched
                      ? (field.value ?? []).map(() => ({ label: true }))
                      : undefined

                    return (
                      <QuestionBuilder
                        questions={field.value ?? []}
                        onChange={questions => field.onChange(questions)}
                        errors={questionErrors}
                        touched={questionsTouched}
                        setFieldTouched={() => field.onBlur()}
                        maxQuestions={maxQuestions}
                        allowedQuestionTypes={allowedQuestionTypes}
                        lastQuestionRemoval="clear"
                        headerTitle={t('typography.templates.form.survey.questionsTitle')}
                        headerDescription={t('typography.templates.form.survey.questionsDescription', {
                          max: maxQuestions
                        })}
                        addQuestionButton={t('typography.templates.form.survey.addQuestion')}
                        defaultRatingMinLabel={t('typography.templates.form.survey.defaultRatingMinLabel')}
                        defaultRatingMaxLabel={t('typography.templates.form.survey.defaultRatingMaxLabel')}
                        typeLabel={t('typography.templates.form.survey.typeLabel')}
                        questionLabel={t('typography.templates.form.survey.questionLabel')}
                        requiredLabel={t('typography.templates.form.survey.requiredLabel')}
                        questionPreviewLabel={t('typography.templates.form.survey.questionPreviewLabel')}
                        selectTypePlaceholder={t('typography.templates.form.survey.selectTypePlaceholder')}
                        ratingQuestionPlaceholder={t('typography.templates.form.survey.ratingQuestionPlaceholder')}
                        freeTextQuestionPlaceholder={t('typography.templates.form.survey.freeTextQuestionPlaceholder')}
                        removeButton={t('typography.templates.form.survey.removeButton')}
                        ratingTypeLabel={t('typography.templates.form.survey.ratingTypeLabel')}
                        freeTextTypeLabel={t('typography.templates.form.survey.freeTextTypeLabel')}
                        activeBadgeLabel={t('typography.templates.form.survey.activeBadgeLabel')}
                        deactivatedBadgeLabel={t('typography.templates.form.survey.deactivatedBadgeLabel')}
                        deactivateButtonLabel={t('typography.templates.form.survey.deactivateButtonLabel')}
                        activateButtonLabel={t('typography.templates.form.survey.activateButtonLabel')}
                      />
                    )
                  }}
                />
              </Box>
            )}
          </Flex>
        )}
      </FullPageFormLayout>

      <Modal
        open={!!resultState}
        onClose={resultState === 'success' ? handleBack : handleFailureClose}
        data-testid="survey-template-result-modal"
      >
        {resultState === 'success' && (
          <AcknowledgeModal
            result="positive"
            title={
              isEdit
                ? t('typography.templates.form.editSuccessTitle')
                : t('typography.templates.form.createSuccessTitle')
            }
            description={t('typography.templates.form.saveSurveySuccessDescription')}
            buttonText={t('button.close')}
            handleClose={handleBack}
            viewTestId="survey-template-success"
            buttonTestId="survey-template-success-close"
          />
        )}
        {resultState === 'failure' && (
          <AcknowledgeModal
            result="negative"
            title={
              isEdit ? t('typography.templates.form.editErrorTitle') : t('typography.templates.form.createErrorTitle')
            }
            description={t('typography.templates.form.saveErrorDescription')}
            buttonText={t('button.close')}
            handleClose={handleFailureClose}
            viewTestId="survey-template-error"
            buttonTestId="survey-template-error-close"
          />
        )}
      </Modal>
    </Box>
  )
}
